import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, ChevronDown, ChevronUp, Shield, Palette, HandHeart } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const PersonCard = ({ person }) => {
  const [expanded, setExpanded] = useState(false);
  const hasBio = person.bio && person.bio !== 'Profile currently in development.' && person.bio !== 'Expanded biography currently in development.';
  const inDev = !hasBio;

  return (
    <div className="bg-white border border-gray-100 p-6 lg:p-8" data-testid={`person-${person.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-brand-dark">{person.name}</h3>
          <p className="text-brand-gold text-sm font-medium mt-0.5">{person.title}</p>
        </div>
      </div>

      {inDev && (
        <p className="text-gray-400 text-sm italic mt-4">Profile currently in development.</p>
      )}

      {hasBio && (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mt-4">
            {expanded ? person.bio : person.bio.slice(0, 220) + (person.bio.length > 220 ? '...' : '')}
          </p>
          {person.bio.length > 220 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-brand-gold text-sm font-medium mt-3 hover:underline"
              data-testid={`toggle-bio-${person.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Read less</> : <><ChevronDown className="w-3.5 h-3.5" /> Read more</>}
            </button>
          )}
          {person.motto && (
            <p className="text-brand-gold/80 text-sm font-medium italic mt-4 border-l-2 border-brand-gold/30 pl-3">{person.motto}</p>
          )}
        </>
      )}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, label, title, description }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} className={`text-center mb-10 transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-gold/10 border border-brand-gold/20 mb-4">
        <Icon className="w-5 h-5 text-brand-gold" />
      </div>
      {label && <p className="text-brand-gold text-xs uppercase tracking-widest mb-2">{label}</p>}
      <h2 className="text-2xl font-semibold text-brand-dark mb-3">{title}</h2>
      {description && <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">{description}</p>}
    </div>
  );
};

const LEADERSHIP = [
  {
    name: 'Justin Toomey',
    title: 'Founder & Executive Director',
    bio: 'Justin Toomey is the Founder and Executive Director of ElevateMN, a Minnesota nonprofit dedicated to strengthening communities through mental wellness, meaningful connection, education, and service. Guided by the belief that healing begins with community, Justin has committed his life to creating spaces where people feel seen, heard, supported, and reminded that they never have to face life\'s challenges alone.\n\nJustin\'s journey has been shaped by resilience, compassion, and an unwavering commitment to serving others. Raised primarily by his mother after his parents separated at a young age, he witnessed firsthand both hardship and perseverance. Those experiences instilled in him empathy, responsibility, and the belief that behind every smile is a story\u2014and that a single act of kindness can change someone\'s life.\n\nProfessionally, Justin built a successful career spanning more than 26 years in Minnesota\'s green industry. Through leadership, business development, operations, and customer service, he developed a reputation for integrity, accountability, and building strong relationships.\n\nEventually, he found himself asking a question that would change the course of his life: "What is all of this for?"\n\nThat question led him toward community service, where countless conversations revealed a common need: people longed to feel connected, understood, and accepted without judgment. Those experiences became the inspiration for ElevateMN.\n\nToday, Justin leads the organization by developing programs that reduce isolation, encourage authentic connection, and improve mental wellness throughout Minnesota. Under his leadership, ElevateMN has launched initiatives including the Elevated Escape Retreat, the Elevate & Escape Men\'s Mental Health Reset, community outreach projects, charitable programs, and the annual Christmas Compassion Toy Drive.\n\nJustin is also a passionate advocate for responsible cannabis education, veteran support, volunteerism, and collaborative partnerships between nonprofits, businesses, educators, healthcare professionals, artists, and community leaders.\n\nAbove all else, Justin considers family to be his greatest priority. He hopes his legacy is measured not by titles or accomplishments, but by the lives touched, the relationships built, and the hope shared with others.',
    motto: 'Be Blessed. Stay Lifted. Spread Love.',
  },
  {
    name: 'Jared Swanson',
    title: 'Co-Founder',
    bio: 'Jared Swanson is a business leader, entrepreneur, and community advocate whose life has been defined by resilience, perseverance, and servant leadership. As Co-Founder of ElevateMN and Chief Operating Officer of Emerald Enterprise & Distribution, Jared is committed to building organizations that create opportunity, strengthen communities, and improve the lives of others.\n\nGrowing up across several states, Jared\'s life was shaped by both tremendous loss and remarkable examples of compassion. His experiences\u2014including overcoming personal adversity, transforming his health by losing more than 165 pounds, and rebuilding through perseverance\u2014continue to influence the leader he is today.\n\nJared began working at the age of fifteen before steadily advancing into restaurant leadership, ultimately becoming a General Manager known for mentoring employees and building high-performing teams. His entrepreneurial journey eventually led him into business ownership while helping establish ElevateMN alongside Justin.\n\nAs Co-Founder, Jared continues helping shape the organization\'s strategic direction while advocating for stronger mental health resources, meaningful partnerships, and community-driven solutions that create lasting impact throughout Minnesota.\n\nAbove everything else, Jared is a devoted husband and father whose family inspires him daily to build a brighter future and leave a legacy grounded in compassion, integrity, and service.',
    motto: 'If there is one thing people remember about him, he hopes it is that he genuinely cared.',
  },
  { name: 'Blair Smith', title: 'Retreat Production Coordinator', bio: 'Profile currently in development.' },
  { name: 'Henry Istre', title: 'Director of Music & Entertainment', bio: 'Profile currently in development.' },
  {
    name: 'Sarah Sattler',
    title: 'Executive Administrative Operations Assistant',
    bio: 'Sarah Sattler helps keep ElevateMN organized, connected, and moving forward. Working alongside the leadership team, she coordinates executive administration, project management, sponsorship support, volunteer engagement, and event logistics to ensure our programs and initiatives are executed with professionalism and purpose.\n\nFrom preparing for the Elevated Escape Camping Retreat to supporting community partnerships and day-to-day operations, Sarah plays an integral role in turning ideas into action. Her organization, attention to detail, and commitment to our mission help strengthen the foundation that allows ElevateMN to continue growing and serving communities across Minnesota.',
  },
  { name: 'Julia Nottingham', title: 'Director of Health & Wellness', bio: 'Profile currently in development.' },
];

const HEALTH_TEAM = [
  { name: 'Molly Taylor, RN', title: 'Volunteer Registered Nurse', bio: 'Profile currently in development.' },
  {
    name: 'Trisha Brown, RN',
    title: 'Volunteer Registered Nurse & Plant Medicine Education Facilitator',
    bio: 'Trisha serves as both a Volunteer Registered Nurse and Plant Medicine Education Facilitator for ElevateMN. She helps support participant well-being during events while leading educational conversations centered on plant medicine, emphasizing responsible learning, thoughtful discussion, and informed decision-making.',
  },
];

const FACILITATORS = [
  {
    name: 'Sarah Sattler',
    title: 'Founder | Studio S Collective (Puff N Paint Experience)',
    bio: 'In addition to her leadership role at ElevateMN, Sarah is the founder of Studio S Collective, a creative experience company dedicated to bringing people together through art and connection. Through her Puff N Paint Experience, she creates welcoming, judgment-free environments where creativity becomes a catalyst for self-expression, conversation, and community.',
  },
  {
    name: 'Mariah Hartmann',
    title: 'Founder | Moonshine Healing',
    bio: 'Mariah is a wellness facilitator, yoga instructor, and creator of the LUNA Alignment Method\u2122, helping participants reconnect with themselves through mindfulness, movement, breathwork, meditation, and nervous system regulation. Her workshops inspire individuals to slow down, reflect, and return to everyday life feeling grounded, aligned, and empowered.',
  },
  {
    name: 'Brian Priebe',
    title: 'Founder | Sound Healing MN',
    bio: 'Brian facilitates immersive sound healing experiences using crystal singing bowls, percussion, gongs, chimes, and other healing instruments to promote relaxation, mindfulness, emotional regulation, and presence. His Nature Sound Journey invites participants to reconnect with both nature and themselves.',
  },
  {
    name: 'Mary Hartmann',
    title: 'Founder | The Shift Academy\u2122',
    bio: 'Mary Hartmann is a Relationship & Mindset Coach, transformational speaker, and founder of The Shift Academy\u2122. Drawing upon decades of experience in coaching, neuroscience, personal development, and relationship education, she empowers individuals to heal emotional patterns, strengthen relationships, and create lasting personal transformation.',
  },
  { name: 'Julia Nottingham', title: 'Health & Wellness Facilitator', bio: 'Profile currently in development.' },
  { name: 'Trisha Brown, RN', title: 'Plant Medicine Education Facilitator', bio: 'Expanded biography currently in development.' },
];

const VOLUNTEERS = [
  { name: 'Richard', title: 'Dedicated Core Volunteer', bio: 'Profile currently in development.' },
  { name: 'Tomas Lewis', title: 'Dedicated Kitchen Volunteer', bio: 'Profile currently in development.' },
  { name: 'Tim Way', title: 'Dedicated Kitchen Volunteer', bio: 'Profile currently in development.' },
  { name: 'Tomas Hoffer', title: 'Dedicated Volunteer', bio: 'Profile currently in development.' },
  { name: 'Aubrey Lundquist', title: 'Dedicated Volunteer', bio: 'Profile currently in development.' },
];

const OurPeoplePage = () => {
  usePageTitle('Our People');
  const [heroRef, heroVisible] = useScrollReveal();
  const [ctaRef, ctaVisible] = useScrollReveal();

  return (
    <div data-testid="our-people-page">
      {/* Hero */}
      <section className="bg-brand-dark py-20 lg:py-28">
        <div ref={heroRef} className={`mx-auto max-w-4xl px-6 lg:px-8 text-center transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-brand-gold text-xs uppercase tracking-widest mb-4">Our People</p>
          <h1 className="text-3xl lg:text-5xl font-semibold text-white mb-6">Healing Starts with Community</h1>
          <p className="text-gray-400 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
            At ElevateMN, we believe healing starts with community. Every workshop, retreat, outreach initiative, charitable program, and community event is made possible by people who believe in serving others with compassion, authenticity, and purpose.
          </p>
          <p className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto">
            Our organization is built upon the belief that meaningful human connection has the power to change lives. Together, our leaders, healthcare professionals, facilitators, and volunteers work to create welcoming spaces where individuals feel supported, encouraged, and empowered to grow.
          </p>
        </div>
      </section>

      {/* Mission & Tagline */}
      <section className="bg-brand-light py-12 border-b border-gray-200">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto mb-4">
            Our programs are built around connection, movement, and shared experience. We create accessible spaces where people feel supported, seen, and empowered.
          </p>
          <p className="text-brand-gold font-semibold text-lg">Elevating Minds. Healing Hearts. Growing Together.</p>
        </div>
      </section>

      {/* Core Leadership */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <SectionHeader icon={Users} label="Leadership" title="Core Leadership Team" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {LEADERSHIP.slice(0, 2).map(p => <PersonCard key={p.name} person={p} />)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {LEADERSHIP.slice(2).map(p => <PersonCard key={p.name} person={p} />)}
          </div>
        </div>
      </section>

      {/* Health & Safety */}
      <section className="bg-brand-light py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <SectionHeader
            icon={Shield}
            label="Health & Safety"
            title="Health & Safety Team"
            description="The health and well-being of our participants remain a top priority. Our volunteer healthcare professionals generously donate their time and expertise to help create safe, supportive environments during ElevateMN workshops, retreats, and community events."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {HEALTH_TEAM.map(p => <PersonCard key={p.name + p.title} person={p} />)}
          </div>
        </div>
      </section>

      {/* Workshop Facilitators */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <SectionHeader
            icon={Palette}
            label="Facilitators"
            title="Workshop Facilitators"
            description="Our workshops are led by professionals, educators, artists, and wellness advocates who believe learning and connection go hand in hand. Every facilitator brings unique experience while creating welcoming spaces where participants can grow personally, creatively, and emotionally."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FACILITATORS.map(p => <PersonCard key={p.name + p.title} person={p} />)}
          </div>
        </div>
      </section>

      {/* Volunteers */}
      <section className="bg-brand-light py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <SectionHeader
            icon={HandHeart}
            label="Volunteers"
            title="Volunteer Team"
            description="Behind every successful workshop, retreat, outreach initiative, and charitable program is a team of volunteers who generously give their time, talents, and hearts in service to others. Their commitment embodies the spirit of ElevateMN and reminds us that meaningful change begins with people willing to step forward and make a difference."
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {VOLUNTEERS.map(p => <PersonCard key={p.name} person={p} />)}
          </div>
        </div>
      </section>

      {/* Join Our Mission CTA */}
      <section className="bg-brand-dark py-16 lg:py-20">
        <div ref={ctaRef} className={`mx-auto max-w-3xl px-6 lg:px-8 text-center transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Heart className="w-8 h-8 text-brand-gold mx-auto mb-4" />
          <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-4">Join Our Mission</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            ElevateMN exists because people choose to serve.
          </p>
          <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            Whether you are interested in volunteering, facilitating a workshop, becoming a community partner, sponsoring an event, or making a donation, there is a place for you within the ElevateMN family.
          </p>
          <p className="text-brand-gold font-semibold mb-8">Elevating Minds. Healing Hearts. Growing Together.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-brand-gold text-brand-dark font-semibold px-8 py-3 hover:opacity-90 transition-opacity" data-testid="people-contact-btn">
              Get Involved
            </Link>
            <Link to="/events" className="border border-white/20 text-white font-semibold px-8 py-3 hover:bg-white/10 transition-colors" data-testid="people-events-btn">
              View Events
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurPeoplePage;
